<template>
	<div class="chat-log h-100 d-flex flex-column overflow-hidden">
		<horizontal-scroller class="chat-alerts-container">
			<TransitionGroup class="chat-alerts" name="wedge" tag="div">
				<div :class="[ 'donation', ...alert.classList ]" v-for="alert in alerts" :key="alert.id" @click="selectedMessage = alert">
					<b-avatar size="1.5em" :src="alert.avatarUrl||alert.serviceIconUrl" badge-variant="none">
						<template #badge><b-avatar size="1.5em" :src="alert.serviceIconUrl" v-if="alert.avatarUrl"/></template>
					</b-avatar>
					<span class="donation-amount" v-if="alert.isGift">{{alert.giftAmount}}</span>
					<span class="short" v-if="alert.shortEvent">{{alert.shortEvent}}</span>
					<div class="progress-bar" :style="`animation-duration: ${alert.animationDuration}s`"></div>
				</div>
			</TransitionGroup>
		</horizontal-scroller>
		<div class="d-flex h-100 flex-fill justify-content-center align-items-center" v-if="loading>0">
			<b-icon icon="circle-fill" animation="throb" font-scale="2"></b-icon>
		</div>
		<div v-show="loading <= 0" ref="chatLog" class="flex-fill overflow-auto dashboardChatLog" @scroll="chatLogScrolled($event)" />
		<div ref="scrollToBottom" class="chatLogScrollToBottom" v-show="historyMode"><b-button @click="bottomOut()"><b-icon icon="arrow-down"></b-icon></b-button></div>
		<chat-events-bar :event="barEvent"></chat-events-bar>
		<div class="chat-log-footer m-2">
			<b-alert v-model="showMessageError" :show="5" variant="danger" dismissible>{{messageError}}</b-alert>
			<b-input v-model="message" placeholder="Send message..." :readonly="messageLoading>0||!channel&&channels?.length==0" autocomplete="off" @keyup.enter.prevent.stop="message && sendMessage()"></b-input>
			<div class="d-flex align-items-center mt-2">
				<b-dropdown dropup size="sm" variant="secondary" class="chat-log-channel" toggle-class="d-flex align-items-center">
					<template #button-content>
						<b-avatar size="1.5rem" :src="channel?channel.avatar_url:logo"></b-avatar>
						<span class="d-none d-sm-inline ml-2">{{channel ? channel.title : 'All channels'}}</span>
					</template>
					<b-dropdown-item @click="channel = null">
						<b-avatar size="2rem" class="mr-1" :src="logo"></b-avatar>
						All channels
					</b-dropdown-item>
					<b-dropdown-item v-for="c, i in channels" :key="i" @click="channel = c">
						<b-avatar size="2rem" :src="c.avatar_url" class="mr-1" badge-variant="dark"><template #badge><img :src="c.service_icon_url" style="width: 1.3em"></template></b-avatar>
						{{c.title}}
					</b-dropdown-item>
				</b-dropdown>
				<div class="ml-auto d-flex align-items-center">
					<b-button size="sm" :variant="user?.highlighted_message ? 'danger' : 'secondary'" class="mr-2" :disabled="!user?.highlighted_message" title="Stop showing the highlighted message" @click="hideHighlightedMessage()"><b-icon icon="x"/><span class="chat-log-btn-label">Hide</span></b-button>
					<message-type-filter
						class="mr-2"
						mode="chat"
						variant="secondary"
						dropup
						v-model="hiddenTypes"
						@clear="clearLog()"
					></message-type-filter>
					<b-button size="sm" variant="success" title="Send message" :disabled="!message || messageLoading > 0" @click="sendMessage()">
						<svg class="chat-log-send-icon" fill="currentColor" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 495 495">
							<g>
								<path d="M164.711,456.687c0,2.966,1.647,5.686,4.266,7.072c2.617,1.385,5.799,1.207,8.245-0.468l55.09-37.616 l-67.6-32.22V456.687z"/>
								<path d="M492.431,32.443c-1.513-1.395-3.466-2.125-5.44-2.125c-1.19,0-2.377,0.264-3.5,0.816L7.905,264.422 c-4.861,2.389-7.937,7.353-7.904,12.783c0.033,5.423,3.161,10.353,8.057,12.689l125.342,59.724l250.62-205.99L164.455,364.414 l156.145,74.4c1.918,0.919,4.012,1.376,6.084,1.376c1.768,0,3.519-0.322,5.186-0.977c3.637-1.438,6.527-4.318,7.97-7.956 L494.436,41.257C495.66,38.188,494.862,34.679,492.431,32.443z"/>
							</g>
						</svg>
						<span class="chat-log-btn-label">Send</span>
					</b-button>
				</div>
			</div>
		</div>
		<message-details-overlay
			:message="selectedMessage"
			:user="user"
			:serviceIcons="serviceIcons"
			@close="selectedMessage = null"
			@select="highlightMessage($event)"
			@replay="replayAlert($event)"
		/>
	</div>
</template>

<script>
import FetchPoller from '@/FetchPoller';
import lj3 from '@/assets/lj3.png';
import util from '@/util';
import giftColorThresholds from '@/GiftThresholds';
import HorizontalScroller from '@/components/HorizontalScroller.vue';
import MessageDetailsOverlay from '@/components/MessageDetailsOverlay.vue';
import MessageTypeFilter from '@/components/MessageTypeFilter.vue';
import ChatEventsBar from '@/components/ChatEventsBar.vue';
import { EVENT_BAR_TYPES, loadHiddenMessageTypes } from '@/logMessageFilter';

// Comings and goings are too noisy for the log itself, so they get their own one-line bar
// above the input instead, which fades out once nothing has happened for a while.
const EVENT_BAR_TIMEOUT_MS = 5 * 60 * 1000;
// Moderation events act on messages already in the log (which they mark up in place) rather
// than being messages of their own, so they never get a line to themselves.
const MODERATION_TYPES = [ 'delete', 'clear' ];

const styleId = 'chat-log-gift-color-thresholds-style';
let style = document.getElementById(styleId);
if(!style) {
	style = document.createElement('style');
	style.type = 'text/css';
	style.id = styleId;
	document.head.appendChild(style);
}

style.innerHTML = giftColorThresholds.map(t => `
	.chat-log .gift-${t.value},
	.chat-log .gift-${t.value} > .card-header,
	.chat-log > .chat-alerts-container .chat-alerts > .donation.gift.gift-${t.value},
	.chat-log > .dashboardChatLog > .message.gift.gift-${t.value}:not(.highlighted) {
		color: ${util.getContrastingTextColor(t.color)};
		background-color: ${t.color};
	}
	.chat-log .gift-${t.value} > .card-body,
	.chat-log > .chat-alerts-container .chat-alerts > .donation.gift.gift-${t.value}:hover,
	.chat-log > .dashboardChatLog > .message.gift.gift-${t.value}:not(.highlighted):hover {
		background-color: ${t.secondaryColor||t.color};
	}
`).join('');

export default {
	props: {
		serviceIcons: Object,
	},
	data() { return {
		// Event types to leave out of the log; owned here because the filter control sits in
		// this component's footer. Loaded up front so the first render is already filtered.
		hiddenTypes: loadHiddenMessageTypes('chat'),
		// The event currently shown in the bar above the input, or null while the bar is hidden.
		barEvent: null,
		loading: 0,
		log: [],
		historyMode: false,
		user: null,
		message: '',
		showMessageError: false,
		messageError: null,
		messageLoading: 0,
		channel: null,
		channels: null,
		logo: lj3,
		selectedMessage: null,
		alerts: [],
	}},
	components: {
		HorizontalScroller,
		MessageDetailsOverlay,
		MessageTypeFilter,
		ChatEventsBar,
	},
	created() {
		this.removedOffset = 0;
		this.scrollPos = 0;
		this.prevHeight = 0;
		this.barEventSeq = 0;
	},
	watch: {
		// Reload from the start so the log shows a full page of the types that are still visible.
		hiddenTypes() {
			this.reloadAlertTicker();
			this.bottomOut();
		},
	},
	async mounted() {
		try {
			this.loading++;
			this.$refs.chatLog.innerHTML = '';
			this.prevHeight = this.$refs.chatLog.clientHeight;
			this.prevScrollHeight = this.$refs.chatLog.scrollHeight;
			this.startScrollAnimation();
			this.channelPoller = new FetchPoller(async signal => {
				try {
					this.error = null;
					await this.loadChannels(signal);
				} catch(e) {
					if(!(e instanceof DOMException) || !(e.code == 20 || e.name == 'AbortError')) {
						this.error = e;
						console.error('Polling channels', e, e instanceof DOMException, e.code, e.name);
					}
				}
			}, 1 * 60 * 1000);
			this.user = await this.$api.get('userData');
			this.$socket.subscribe(this.user.widget_hash, {
				chat: msg => {
					this.pushBarEvent(msg);
					this.markDeletedMessages(msg);
					if(!this.historyMode) this.appendMessages([msg]);
				},
				chatLogCleared: () => this.bottomOut(),
				dashboardMessageStatus: msg => this.updateDashboardMessageStatus(msg),
				channelInfoUpdate: () => this.channelPoller.start(),
				channelDeleted: () => this.channelPoller.start(),
				channelCreated: () => this.channelPoller.start(),
				channelStatusChanged: () => this.channelPoller.start(),
				restreamStatus: () => this.channelPoller.start(),
				userWidgetAlert: msg => {
					if(msg.isReplay) return; // don't append replays
					const prepared = this.prepareAlert(msg.message);
					if(!prepared) return; // filtered out, or already expired
					this.alerts = [
						prepared,
						...this.alerts,
					];
				},
				messageHighlighted: msg => {
					const log = this.$refs.chatLog;
					if(log) for(const h of Array.from(log.getElementsByClassName('highlighted'))) h.classList.remove('highlighted');
					this.user.highlighted_message = msg;
					// The alert log renders the same message (same id) in its own DOM, so a
					// bare getElementById would just grab whichever log happens to come first.
					log?.querySelector(`[id="${CSS.escape(msg.id)}"]`)?.classList.add('highlighted');
				},
				messageUnhighlighted: () => {
					const log = this.$refs.chatLog;
					if(log) for(const h of Array.from(log.getElementsByClassName('highlighted'))) h.classList.remove('highlighted');
					this.user.highlighted_message = null;
				},
			});

			const results = await this.$api.get(`userGiftTypes`);
			this.loadGiftTypes(results.results);
			await this.channelPoller.start(true);
			await this.reloadAlertTicker();
			await this.bottomOut();
		} catch(e) {
			console.error(e)
		} finally {
			this.loading--;
		}
	},
	beforeDestroy() {
		this.stopScrollAnimation();
		this.channelPoller.stop();
		clearTimeout(this.barEventTimer);
	},
	methods: {
		async loadChannels(signal) {
			this.channels = await this.$api.get('channels/chat', null, signal);
		},
		// The alert ticker above the log follows the same type filter as the log itself.
		async reloadAlertTicker() {
			try {
				const results = await this.$api.get('alerts');
				results.reverse();
				this.alerts = results.map(r => this.prepareAlert(r.message, r.sent_at)).filter(a => !!a);
			} catch(e) {
				console.error(e);
			}
		},
		loadGiftTypes(giftTypes) {
			this.giftTypes = {};
			for(let giftType of giftTypes) {
				if(!(giftType.service_id in this.giftTypes))
					this.giftTypes[giftType.service_id] = {};
				this.giftTypes[giftType.service_id][giftType.gift_type] = giftType;
			}
		},
		startScrollAnimation() {
			this.animationRequestId = window.requestAnimationFrame(this.animationFrame.bind(this));
		},
		stopScrollAnimation() {
			if(!this.animationRequestId) return
			window.cancelAnimationFrame(this.animationRequestId);
			delete this.animationRequestId;
		},
		animationFrame() {
			if(!this.animationRequestId) return;
			this.animationRequestId = window.requestAnimationFrame(this.animationFrame.bind(this));
			if(!this.$refs.chatLog) return;
			if(this.historyMode) return;
			let scrollTopMax = this.$refs.chatLog.scrollHeight - this.$refs.chatLog.offsetHeight;
			let scrollDiff = scrollTopMax - this.$refs.chatLog.scrollTop;
			if(scrollDiff > 400) {
				this.$refs.chatLog.scrollTop += scrollDiff;
			} else {
				let scrollMove = Math.round(scrollDiff / 5) + 1;
				this.$refs.chatLog.scrollTop += scrollMove;
			}
		},
		chatLogScrolled(event) {
			const scrollBottom = event.target.scrollHeight - event.target.scrollTop - event.target.clientHeight;
			if(this.historyMode) {
				if(event.target.scrollHeight <= event.target.clientHeight) {
					this.historyMode = false;
				} else {
					if(scrollBottom + this.removedOffset < 1) {
						this.$api.get('chatLog', {
							limit: 100,
							afterId: this.$refs.chatLog.childNodes[this.$refs.chatLog.childNodes.length - 1].dataset.id
						}).then(log => {
							this.appendMessages(log);
							if(log.length < 100)
								this.historyMode = false;
						});
					} else if(event.target.scrollTop + this.removedOffset < 1) {
						this.$api.get('chatLog', {
							limit: 100,
							beforeId: this.$refs.chatLog.childNodes[0].dataset.id
						}).then(log => {
							const prevScrollHeight = event.target.scrollHeight;
							this.prependMessages(log);
							event.target.scrollTop = event.target.scrollHeight + this.removedOffset - prevScrollHeight;
						});
					}
				}
			} else {
				const scrolledPx = event.target.scrollTop - this.scrollPos;
				const resizedPx = event.target.clientHeight - this.prevHeight;
				const innerPx = event.target.scrollHeight - this.prevScrollHeight;
				if(innerPx == 0 && resizedPx == 0 && this.removedOffset == 0 && scrolledPx < 0)
					this.historyMode = true;
			}
			this.removedOffset = 0;
			this.scrollPos = event.target.scrollTop;
			this.prevHeight = this.$refs.chatLog.clientHeight;
			this.prevScrollHeight = this.$refs.chatLog.scrollHeight;
		},
		async bottomOut() {
			try {
				this.error = null;
				this.historyMode = false;
				// this.$refs.chatLog.scrollTop = this.$refs.chatLog.scrollTopMax || (this.$refs.chatLog.scrollHeight - this.$refs.chatLog.clientHeight);
				// With a filter on most of a page can be invisible, so ask for more and keep the
				// newest 200 that survive it.
				const log = await this.$api.get('chatLog', { limit: this.hiddenTypes.length ? 1000 : 200 });
				const visible = log.filter(m => !this.isHiddenFromLog(m)).slice(0, 200).reverse();
				this.$refs.chatLog.innerHTML = '';
				this.appendMessages(visible, true);
				this.historyMode = false;
				this.$refs.chatLog.scrollTop = this.$refs.chatLog.scrollTopMax || (this.$refs.chatLog.scrollHeight - this.$refs.chatLog.clientHeight);
				await util.awaitMediaLoadedRecursive(this.$refs.chatLog);
				this.historyMode = false;
				this.$refs.chatLog.scrollTop = this.$refs.chatLog.scrollTopMax || (this.$refs.chatLog.scrollHeight - this.$refs.chatLog.clientHeight);
			} catch(e) {
				console.error(e);
				this.error = e;
			}
		},
		isMessageHidden(msg) {
			return this.hiddenTypes.includes(util.resolveEventType(msg));
		},
		// The log proper also leaves out whatever is represented elsewhere in the panel: the
		// events bar's own types, and moderation events that only mark up existing messages.
		isHiddenFromLog(msg) {
			return EVENT_BAR_TYPES.includes(msg?.type)
				|| MODERATION_TYPES.includes(msg?.type)
				|| this.isMessageHidden(msg);
		},
		// A delete names the platform tokens it removed; the log keys its rows by
		// serviceId-token, so whichever ones are still on screen can be struck through.
		markDeletedMessages(msg) {
			if(msg?.type != 'delete' || !msg.ids?.length) return;
			const log = this.$refs.chatLog;
			if(!log) return;
			for(const id of msg.ids)
				log.querySelector(`[id="${CSS.escape(`${msg.serviceId}-${id}`)}"]`)?.classList.add('deleted');
		},
		// Deliberately independent of the type filter: these events are hidden from the log by
		// default, and this bar is where they are meant to show up instead.
		pushBarEvent(msg) {
			if(!EVENT_BAR_TYPES.includes(msg?.type)) return;
			const prepared = util.prepareMessage(msg, {
				avatarUrl: true,
				serviceIconUrl: true,
				eventString: true,
			}, {
				serviceIcons: this.serviceIcons,
				user: this.user,
			});
			// Two events in a row can share an id (or have none at all); the bar needs a key that
			// always changes so the outgoing one actually animates away.
			prepared.eventKey = ++this.barEventSeq;
			this.barEvent = prepared;
			clearTimeout(this.barEventTimer);
			this.barEventTimer = setTimeout(() => { this.barEvent = null; }, EVENT_BAR_TIMEOUT_MS);
		},
		async clearLog() {
			const confirmed = await this.$bvModal.msgBoxConfirm('This permanently deletes your stored chat log.', {
				title: 'Clear chat log',
				size: 'sm',
				buttonSize: 'sm',
				okVariant: 'danger',
				okTitle: 'Clear',
				footerClass: 'p-2',
				centered: true,
			});
			if(!confirmed) return;
			try {
				await this.$api.delete('chatLog');
				await this.bottomOut();
			} catch(e) {
				console.error(e);
				this.$bvToast.toast('Could not clear the chat log: '+(e.error||String(e)), {
					title: 'Error',
					variant: 'danger',
					solid: true,
				});
			}
		},
		appendMessages(msgs, noRemove) {
			if(!this.$refs.chatLog) return;
			const log = this.$refs.chatLog;
			// append new elements at the bottom
			const fragment = document.createDocumentFragment();
			for(let msg of msgs) {
				if(this.isHiddenFromLog(msg)) continue;
				try {
					const elem = this.createMessageElem(msg);
					if(elem) fragment.appendChild(elem);
				} catch(e) {
					console.error(e);
				}
			}
			log.appendChild(fragment);
			if(noRemove) return;
			// remove old elements from the top
			const last = log.childNodes[log.childNodes.length - 200];
			if(last)
				this.removedOffset = last.offsetTop + last.offsetHeight - log.firstChild.offsetTop;
			// create a ghost fragment which gets garbage collected
			const fragment2 = document.createDocumentFragment();
			while(log.childNodes.length > 200)
				fragment2.appendChild(log.firstChild);
		},
		prependMessages(msgs) {
			if(!this.$refs.chatLog) return;
			const log = this.$refs.chatLog;
			// prepend new messages at top
			const fragment = document.createDocumentFragment();
			for(let i = msgs.length - 1; i >= 0; i--) {
				if(this.isHiddenFromLog(msgs[i])) continue;
				try {
					const elem = this.createMessageElem(msgs[i]);
					if(elem) fragment.appendChild(elem);
				} catch(e) {
					console.error(e);
				}
			}
			log.prepend(fragment);
			// remove old messages from bottom (only ever more than 200 with something prepended)
			if(log.childNodes.length <= 200) return;
			this.removedOffset = log.lastChild.offsetTop + log.lastChild.offsetHeight - log.childNodes[200].offsetTop;
			const fragment2 = document.createDocumentFragment();
			while(log.childNodes.length > 200)
				fragment2.appendChild(log.lastChild);
		},
		createMessageElem(msg) {
			return util.createChatLogMessage(msg, {
				serviceIcons: this.serviceIcons,
				user: this.user,
				giftTypes: this.giftTypes,
				channels: this.channels,
				giftColorThresholds,
			}, {
				onclick: prepared => { this.selectedMessage = prepared; },
				onmenuclick: prepared => this.highlightMessage(prepared),
			}, this.$refs.chatLog);
		},

		// Dashboard message per-platform status arrives over the socket; update the plain-DOM
		// indicator in place (the chat log bypasses Vue for performance).
		updateDashboardMessageStatus({ dbId, index, status, error }) {
			const elem = this.$refs.chatLog?.querySelector(`[data-id="${dbId}"]`);
			util.updateDashboardIndicator(elem, index, status, error);
		},

		highlightMessage(msg) {
			// Not document.getElementById: the alert log renders this same message (same id)
			// in its own DOM, so a bare lookup could grab that copy instead of this one.
			const target = this.$refs.chatLog?.querySelector(`[id="${CSS.escape(msg.id)}"]`);
			target?.classList.add('loading');
			const is_current = String(msg.serviceId) == String(this.user.highlighted_message?.serviceId) && msg.token == this.user.highlighted_message?.token;
			this.$api.post('selectMessage', is_current ? null : msg).then(() => {
				const highlighted = this.$refs.chatLog?.getElementsByClassName('highlighted');
				highlighted?.forEach?.(h => h.classList.remove('highlighted'));
				if(is_current) {
					this.user.highlighted_message = null;
				} else {
					target?.classList.add('highlighted');
					this.user.highlighted_message = msg;
				}
			}).catch(err => {
				this.$bvToast.toast('Could not select message: '+(err.error||err.toString()), {
					title: 'Error',
					variant: 'danger',
					solid: true,
				});
			}).finally(() => {
				target?.classList.remove('loading');
			});
			this.$broadcastChannel?.postMessage({
				type: 'highlightedMessage',
				data: is_current ? null : JSON.parse(JSON.stringify(msg)),
			});
		},

		hideHighlightedMessage() {
			this.$api.post('selectMessage', null).then(() => {
				const highlighted = this.$refs.chatLog.getElementsByClassName('highlighted');
				for(let h of highlighted)
					h.classList.remove('highlighted');
				this.user.highlighted_message = null;
			}).catch(err => {
				this.$bvToast.toast('Could not hide message: '+(err.error||err.toString()), {
					title: 'Error',
					variant: 'danger',
					solid: true,
				});
			});
			this.$broadcastChannel?.postMessage({
				type: 'highlightedMessage',
				data: null,
			});
		},

		async sendMessage() {
			try {
				const message = this.message;
				this.message = '';
				this.showMessageError = false;
				this.messageError = null;
				this.messageLoading++;
				if(this.channel) {
					// Single channel selected: plain synchronous send, no stored message / indicators.
					await this.$api.post('message', {
						serviceId: this.channel.service_id,
						channelToken: this.channel.token,
						message,
					});
				} else {
					// All channels: the server stores + shows the message immediately (via the 'chat'
					// socket event) with one indicator per target channel; per-channel send status
					// then arrives over 'dashboardMessageStatus'.
					await this.$api.post('dashboardMessage', { message });
				}
			} catch(e) {
				console.error(e);
				this.showMessageError = true;
				this.messageError = e.message;
			} finally {
				this.messageLoading--;
			}
		},
		prepareAlert(msg, sent_at) {
			if(this.isMessageHidden(msg)) return;
			const prepared = util.prepareMessage(msg, {
				serviceIcon: true, serviceName: true, serviceIconUrl: true,
				displayNameElem: true,
				classList: true,
				spansDom: true,
				giftElem: true, giftColorThreshold: true,
				eventString: true, shortEvent: true,
				canReply: true,
				avatarIcon: true, avatar: true,
				giftAmount: true,
				isGift: true, isAlert: true,
			}, {
				serviceIcons: this.serviceIcons,
				giftColorThresholds,
				user: this.user,
				giftTypes: this.giftTypes,
				channels: this.channels,
			});

			prepared.messages = msg.messages?.map(m => m.message);

			const now_ms = new Date().getTime();
			const sent_at_ms = sent_at ? new Date(sent_at).getTime() : now_ms;
			const duration_sec = Math.max(120, prepared.giftColorThreshold?.duration || 0);
			const expires_at_ms = sent_at_ms + duration_sec * 1000;
			if(expires_at_ms < now_ms) return;
			setTimeout(() => {
				const idx = this.alerts.findIndex(a => a.id == prepared.id);
				if(idx < 0) return;
				this.alerts.splice(idx, 1);
			}, expires_at_ms - now_ms);
			prepared.animationDuration = Math.floor((expires_at_ms - now_ms) / 1000);

			return prepared;
		},
		replayAlert(msg) {
			this.$api.post('alerts/replay', { serviceId: msg.serviceId, token: msg.token });
		},
	}
}
</script>

<style lang="scss">
@import '../plugins/variable.scss';

.chat-log {
	.sticker, .image { max-width: 80px; max-height: 80px; }

	// One or two gift images float beside the text; the moment a 3rd shows up, :has()
	// switches the whole block to its own centered row below the text instead — no
	// JS-picked class needed. Each further :has(:nth-child(N)) rule then shrinks that row
	// a notch more: they're listed smallest-N-first, and since e.g. a 10-image block
	// matches all of them simultaneously, the largest N present wins the cascade (equal
	// specificity, later wins). Same technique GiftWidget.js uses for its own combo stack.
	.chatImages { float: right; }
	.chatImages > .chatImage { max-width: 80px; max-height: 80px; }
	.chatImages:has(.chatImage:nth-child(3)) {
		float: none;
		clear: both;
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		align-items: center;
		gap: 2px;
		margin-top: 2px;
	}
	.chatImages:has(.chatImage:nth-child(3)) > .chatImage { max-width: 48px; max-height: 48px; }
	.chatImages:has(.chatImage:nth-child(4)) > .chatImage { max-width: 40px; max-height: 40px; }
	.chatImages:has(.chatImage:nth-child(5)) > .chatImage { max-width: 36px; max-height: 36px; }
	.chatImages:has(.chatImage:nth-child(6)) > .chatImage { max-width: 32px; max-height: 32px; }
	.chatImages:has(.chatImage:nth-child(7)) > .chatImage { max-width: 28px; max-height: 28px; }
	.chatImages:has(.chatImage:nth-child(8)) > .chatImage { max-width: 26px; max-height: 26px; }
	.chatImages:has(.chatImage:nth-child(9)) > .chatImage { max-width: 24px; max-height: 24px; }
	.chatImages:has(.chatImage:nth-child(10)) > .chatImage { max-width: 22px; max-height: 22px; }
	.chatImages > .chatImages-overflow {
		font-weight: bold;
		opacity: 0.85;
		padding: 0 2px;
	}
	.clearfix { clear: both; }
	.emote { max-width: 32px; max-height: 32px; }

	> .chat-log-footer {
		flex: 0 0 auto;
		// Sized by the panel, not the window, so the controls below drop their labels when
		// this panel is narrow even if the window is wide. Containment also makes the footer
		// a stacking context, hence the z-index: without it the scroll-to-bottom button
		// (z-index 2 in the log's context) would paint over an open dropup menu.
		container-type: inline-size;
		position: relative;
		z-index: 3;

		.chat-log-send-icon {
			width: 14px;
			height: 14px;
		}
		.chat-log-btn-label { margin-left: .2rem; }

		// Too narrow for words: the buttons keep their icons (and their tooltips) and the
		// channel dropup keeps its avatar, which is what identifies it anyway.
		@container (max-width: 260px) {
			.chat-log-channel-title,
			.chat-log-btn-label { display: none; }
		}

		// The channel name shares the row with the send controls, so it gives way first.
		.chat-log-channel {
			min-width: 0;

			> .btn.dropdown-toggle {
				max-width: 100%;
				overflow: hidden;
			}
			.chat-log-channel-title {
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
			}
		}
	}

	> .chat-alerts-container {
		.chat-alerts {
			display: flex;
			animation: scroll-left linear infinite;
			flex-direction: row;

			> .donation {
				cursor: pointer;
				background: $gray-800;
				color: $vaporwave-white;
				border-radius: .4em;
				padding: .3em;
				margin: .2em;
				display: flex;
				gap: .4em;
				flex-direction: row;
				align-items: center;
				font-weight: bold;
				white-space: nowrap;
				position: relative;

				&.gift {
					background: $gold;
				}

				.progress-bar {
					position: absolute;
					bottom: 0;
					left: 0;
					height: 4px;
					background: limegreen;
					width: 100%;
					animation: progressDrain 10s linear forwards;
				}

				/* Keyframes */
				@keyframes progressDrain {
					from { width: 100%; }
					to { width: 0%; }
				}

				@keyframes fadeOut {
					to { opacity: 0; transform: scale(0.9); }
				}
			}
		}
	}
	> .dashboardChatLog {
		word-break: break-word;
		transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

		> .message {
			cursor: pointer;
			padding: 2px 4px;
			position: relative;
			img {
				max-width: 100%;
			}
			&:hover {
				background: rgba(255, 255, 255, .1);
				&:before {
					position: absolute;
					left: 0;
					right: 0;
					top: 0;
					bottom: 0;
					text-align: center;
					background: rgba(255, 255, 255, .2);
					backdrop-filter: blur(3px);
					display: flex;
					align-items: center;
					justify-content: center;
					padding-left: 10px;
					padding-right: 10px;
					color: white;
				}
			}
			&.gift, &.product, &.tower {
				background: $gold;
				color: $gold-text;
				margin: 4px;
				border-radius: 4px;
				padding: 4px;
				&:hover {
					background: darken($gold, 5%);
				}
			}
			&.subscription, &.subGiftReceive {
				background: #0f9d58;
				color: white;
				margin: 4px;
				border-radius: 4px;
				padding: 4px;
				&:hover {
					background: #0a8043;
				}
			}
			// On colored alert backgrounds the default link color is low-contrast
			// (green-on-green for memberships); use the alert's own text color, still underlined.
			&.gift a, &.gift a:hover,
			&.product a, &.product a:hover,
			&.tower a, &.tower a:hover,
			&.subscription a, &.subscription a:hover,
			&.subGiftReceive a, &.subGiftReceive a:hover { color: inherit; text-decoration: underline; }
			&.highlighted {
				background: $vaporwave-white;
				color: $dark;
				&:hover { background: darken($vaporwave-white, 5%); }
			}
			// A deleted message keeps its sender and its place in the log, but says only that
			// it was deleted until clicked; revealing it strikes the text through.
			&.deleted {
				// The clearfix is a block, so it would push the placeholder onto its own line.
				> .content, > .chatImages, > .media, > .clearfix { display: none; }
				&:after {
					content: '(deleted — click to show)';
					font-style: italic;
					opacity: .6;
				}
				&.deleted-revealed {
					> .content, > .chatImages, > .media { display: inline; }
					> .clearfix { display: block; }
					> .content { text-decoration: line-through; opacity: .7; }
					&:after { content: none; }
				}
			}
			&.dashboard-message {
				background: $gray-400;
				&:hover { background: $gray-600; }

				.dashboard-message-targets {
					display: flex;
					flex-wrap: wrap;
					gap: 0.5em;
					margin-top: 0.25em;
					padding-left: 0.25em;
					opacity: 0.9;
				}
				.dashboard-message-target {
					position: relative;
					display: inline-flex;
					align-items: center;
					gap: 0.15em;

					.dmt-service-icon {
						width: 1.1em;
						height: 1.1em;
						border-radius: 2px;
						object-fit: cover;
					}
					.dmt-status { display: inline-flex; font-size: 0.9em; }
					.dmt-icon-success { color: $green; }
					.dmt-icon-error { color: $red; }
					.dmt-icon-sending {
						color: $text-muted;
						animation: dmt-spin 0.8s linear infinite;
					}

					&.dmt-has-error { cursor: help; }
					.dmt-error-popover {
						display: none;
						position: absolute;
						bottom: 100%;
						left: 0;
						z-index: 10;
						margin-bottom: 4px;
						padding: 4px 8px;
						border-radius: 4px;
						background: $red;
						color: #fff;
						font-size: 0.8em;
						white-space: normal;
						width: max-content;
						max-width: 220px;
						box-shadow: 0 2px 6px rgba(0, 0, 0, .3);
						pointer-events: none;
					}
					&.dmt-has-error:hover .dmt-error-popover { display: block; }
				}
			}
			@keyframes dmt-spin { to { transform: rotate(360deg); } }
			> .avatar, > .service {
				width: 24px;
				height: 24px;
				object-fit: cover;
				overflow: hidden;
			}
			> .avatar {
				border-radius: 12px;
			}
			> .name:after { content: ' ' }
			&.chat > .name:after { content: ': ' }
			&.chat.gift > .name:after { content: ' ' }
			&.chat.product > .name:after { content: ' ' }
			> .content {
				display: inline;
				> img {
					max-width: 80px;
					max-height: 80px;

					&.emote {
						max-width: 32px;
						max-height: 32px;
					}
				}
			}
			&.self {
				color: fade-out($vaporwave-white, .4);
				&.highlighted {
					color: $dark;
				}
			}
			&.owner {
				color: $vaporwave-green;
				&.highlighted {
					color: $dark;
				}
			}
			> div.menubtn {
				display: none;
				position: absolute;
				font-size: 15px;
				right: 0;
				top: 0;
				bottom: 0;
				background: transparent;
				line-height: 100%;
				align-items: center;
				padding: 4px 24px;
				text-align: center;
				color: white;
				&:after {
					content: '⊕ Show';
				}
				backdrop-filter: blur(3px);
				z-index: 1;
			}
			&.highlighted {
				> div.menubtn {
					&:hover {
					}
					&:after {
						content: '⊖ Hide';
					}
					color: $dark;
				}
			}
			&:hover > div.menubtn {
				display: inline-flex;
			}
			&:hover > div.menubtn:hover {
				background: rgba(255, 255, 255, .1);
			}
		}
	}

	// Takes no space of its own directly under the scroll area and lifts its button back up
	// into it, so it stays pinned to the bottom of the log whatever the footer's height is.
	> .chatLogScrollToBottom {
		position: relative;
		z-index: 2;
		height: 0;
		text-align: center;
		pointer-events: none;

		> button {
			transform: translateY(calc(-100% - .4rem));
			pointer-events: auto;
		}
	}

	.media {
		font-size: .7em;

		&:before {
			content: '▶︎ ';
		}
	}


	/* Entering Animation */
	.wedge-enter-active {
		transition: opacity 0.5s ease;
	}
	.wedge-enter, .wedge-leave-to {
		opacity: 0;
	}
	.wedge-enter-to {
		opacity: 1;
	}

	/* Leaving Animation */
	.wedge-leave-active {
		position: absolute;
		transition: opacity 0.5s ease;
	}
}
</style>
